/**
 * Persist last N searches and categories for home feed personalization (Phase 2).
 * Stored in localStorage; sent as query params to GET /feed/home.
 * Keyed by userId so different users (e.g. same browser, different accounts) get separate history.
 */

const STORAGE_RECENT_SEARCHES_PREFIX = "ezyhub_recent_searches";
const STORAGE_RECENT_CATEGORIES_PREFIX = "ezyhub_recent_categories";
const MAX_RECENT = 5;

function storageKey(prefix: string, userId: string | undefined | null): string {
  return `${prefix}_${userId?.trim() || "anon"}`;
}

function getStoredStrings(key: string): string[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === "string").slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

function pushAndTrim(values: string[], newVal: string, max: number): string[] {
  const trimmed = newVal.trim();
  if (!trimmed) return values;
  const next = [trimmed, ...values.filter((v) => v !== trimmed)].slice(0, max);
  return next;
}

function save(key: string, values: string[]): void {
  try {
    localStorage.setItem(key, JSON.stringify(values));
  } catch {
    // ignore quota or other storage errors
  }
}

/** Last N search queries (newest first). Pass userId so each user has their own history. */
export function getRecentSearches(userId?: string | null): string[] {
  return getStoredStrings(storageKey(STORAGE_RECENT_SEARCHES_PREFIX, userId));
}

/** Last N category ids or names (newest first). Pass userId so each user has their own history. */
export function getRecentCategories(userId?: string | null): string[] {
  return getStoredStrings(storageKey(STORAGE_RECENT_CATEGORIES_PREFIX, userId));
}

/** Record a search query for personalization. Call when user performs a search. */
export function pushRecentSearch(query: string, userId?: string | null): void {
  const key = storageKey(STORAGE_RECENT_SEARCHES_PREFIX, userId);
  const next = pushAndTrim(getStoredStrings(key), query.trim(), MAX_RECENT);
  save(key, next);
}

/** Record a category for personalization. Call when user selects a category (e.g. not "Todos"). */
export function pushRecentCategory(category: string, userId?: string | null): void {
  if (!category || category === "Todos") return;
  const key = storageKey(STORAGE_RECENT_CATEGORIES_PREFIX, userId);
  const next = pushAndTrim(getStoredStrings(key), category, MAX_RECENT);
  save(key, next);
}
